import { useState, useCallback, useRef, useEffect } from 'react'
import { useMutation, useQuery } from 'convex/react'
import { api } from '../../convex/_generated/api'
import type { Id } from '../../convex/_generated/dataModel'

export function useCommunityChat() {
  const [replyTo, setReplyTo] = useState<{ _id: Id<'communityMessages'>; authorName: string; content: string } | null>(null)
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isTypingRef = useRef(false)

  const messagesResult = useQuery(api.community.listMessages, { limit: 100 })
  const typingUsers = useQuery(api.community.getTypingUsers, {})
  const unreadCount = useQuery(api.community.getUnreadCount, {})
  const isMember = useQuery(api.community.isMember, {})
  const members = useQuery(api.community.listMembers, {})

  const joinMutation = useMutation(api.community.join)
  const leaveMutation = useMutation(api.community.leave)
  const sendMutation = useMutation(api.community.sendMessage)
  const toggleReactionMutation = useMutation(api.community.toggleReaction)
  const setTypingMutation = useMutation(api.community.setTyping)
  const markSeenMutation = useMutation(api.community.markSeen)
  const editMutation = useMutation(api.community.editMessage)
  const deleteMutation = useMutation(api.community.deleteMessage)
  const shadowBanMutation = useMutation(api.community.shadowBanUser)
  const unShadowBanMutation = useMutation(api.community.unShadowBanUser)
  const unhideMutation = useMutation(api.community.unhideMessage)

  const join = useCallback(() => joinMutation(), [joinMutation])
  const leave = useCallback(() => leaveMutation(), [leaveMutation])

  const markSeen = useCallback(() => {
    markSeenMutation().catch(() => {})
  }, [markSeenMutation])

  const sendMessage = useCallback(async (content: string) => {
    if (!content.trim()) return
    await sendMutation({
      content,
      replyTo: replyTo?._id,
    })
    setReplyTo(null)
  }, [sendMutation, replyTo])

  const toggleReaction = useCallback((messageId: Id<'communityMessages'>, emoji: string) => {
    toggleReactionMutation({ messageId, emoji }).catch(() => {})
  }, [toggleReactionMutation])

  const editMessage = useCallback((messageId: Id<'communityMessages'>, content: string) => {
    editMutation({ messageId, content }).catch(() => {})
  }, [editMutation])

  const deleteMessage = useCallback((messageId: Id<'communityMessages'>) => {
    deleteMutation({ messageId }).catch(() => {})
  }, [deleteMutation])

  const adminHideMessage = useCallback((messageId: Id<'communityMessages'>) => {
    deleteMutation({ messageId, adminHide: true }).catch(() => {})
  }, [deleteMutation])

  const unhideMessage = useCallback((messageId: Id<'communityMessages'>) => {
    unhideMutation({ messageId }).catch(() => {})
  }, [unhideMutation])

  const shadowBanUser = useCallback((userId: string) => {
    shadowBanMutation({ userId: userId as Id<'users'> }).catch(() => {})
  }, [shadowBanMutation])

  const unShadowBanUser = useCallback((userId: string) => {
    unShadowBanMutation({ userId: userId as Id<'users'> }).catch(() => {})
  }, [unShadowBanMutation])

  const updateTyping = useCallback((isTyping: boolean) => {
    if (isTyping && !isTypingRef.current) {
      isTypingRef.current = true
      setTypingMutation({ isTyping: true }).catch(() => {})
    }

    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current)

    if (isTyping) {
      typingTimeoutRef.current = setTimeout(() => {
        isTypingRef.current = false
        setTypingMutation({ isTyping: false }).catch(() => {})
      }, 3000)
    } else {
      isTypingRef.current = false
      setTypingMutation({ isTyping: false }).catch(() => {})
    }
  }, [setTypingMutation])

  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current)
      if (isTypingRef.current) {
        setTypingMutation({ isTyping: false }).catch(() => {})
      }
    }
  }, [setTypingMutation])

  return {
    messages: messagesResult?.messages ?? [],
    lastSeenAt: messagesResult?.lastSeenAt,
    isViewerAdmin: messagesResult?.isViewerAdmin ?? false,
    members: members ?? [],
    typingUsers: typingUsers ?? [],
    unreadCount: unreadCount ?? 0,
    isMember: isMember ?? false,
    isLoading: messagesResult === undefined,
    replyTo,
    setReplyTo,
    join,
    leave,
    sendMessage,
    toggleReaction,
    editMessage,
    deleteMessage,
    adminHideMessage,
    unhideMessage,
    shadowBanUser,
    unShadowBanUser,
    updateTyping,
    markSeen,
  }
}
